import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Copy, Sparkles } from 'lucide-react';

import { api } from '@/lib/api';
import { Combobox } from '@/components/Combobox';
import { TagsCombobox } from '@/components/TagsCombobox';
import { RoleTagsCombobox } from '@/components/RoleTagsCombobox';
import { usePhotoStagingStore } from '@/features/tastings/photoStagingStore';
import { StorageLocationPicker } from '@/features/locations/StorageLocationPicker';

// Roles que se pueden asignar a un productor o enólogo de un vino. El backend
// los guarda como string libre en producer_wine.role / enologist_wine.role.
const PERSON_ROLES = [
  { value: 'principal', label: 'principal' },
  { value: 'enologo', label: 'enólogo/a' },
  { value: 'agronomo', label: 'agrónomo/a' },
  { value: 'asesor', label: 'asesor/a' },
  { value: 'consultor', label: 'consultor/a' },
  { value: 'co-producer', label: 'co-producer' },
  { value: 'joint-venture', label: 'joint-venture' },
];
import {
  useEnologists,
  useGrapeVarieties,
  usePlaces,
  useProducers,
  useSaveWine,
  useWine,
  useWineries,
  useWines,
} from './useWines';

interface FormState {
  name: string;
  winery_id: number | '';
  winery_name_new: string;
  place_id: number | '';
  place_path_new: string;
  vintage: number | '';
  type: string;
  classification: string;
  alcohol: number | '';
  ph: number | '';
  inv_number: string;
  external_ref: string;
  format: string;
  bottle_shape: string;
  notes: string;
  grape_varieties: { id: number; percentage?: number }[];
  producers: { id: number; role?: string | null }[];
  enologists: { id: number; role?: string | null }[];
}

interface PurchaseFormState {
  enabled: boolean;
  purchase_date: string;
  quantity: number | '';
  unit_price: number | '';
  currency: string;
  supplier_name: string;
  location_id: number | null;
  notes: string;
}

const today = () => new Date().toISOString().slice(0, 10);

const emptyPurchase: PurchaseFormState = {
  enabled: false,
  purchase_date: today(),
  quantity: '',
  unit_price: '',
  currency: 'ARS',
  supplier_name: '',
  location_id: null,
  notes: '',
};

const empty: FormState = {
  name: '',
  winery_id: '',
  winery_name_new: '',
  place_id: '',
  place_path_new: '',
  vintage: '',
  type: 'tinto',
  classification: 'varietal',
  alcohol: '',
  ph: '',
  inv_number: '',
  external_ref: '',
  format: '750 ml',
  bottle_shape: '',
  notes: '',
  grape_varieties: [],
  producers: [],
  enologists: [],
};

const BOTTLE_FORMATS = [
  { value: '187 ml', label: '187 ml — Piccolo / Benjamín' },
  { value: '375 ml', label: '375 ml — Fillette / Demi' },
  { value: '750 ml', label: '750 ml — Estándar' },
  { value: '1500 ml', label: '1500 ml — Magnum' },
  { value: '3000 ml', label: '3000 ml — Doble Magnum' },
  { value: '4000 ml', label: '4000 ml — Jeroboam' },
  { value: '4500 ml', label: '4500 ml — Rehoboam' },
  { value: '6000 ml', label: '6000 ml — Matusalem / Imperial' },
  { value: '9000 ml', label: '9000 ml — Salmanazar' },
  { value: '12000 ml', label: '12000 ml — Baltasar' },
  { value: '15000 ml', label: '15000 ml — Nabucodonosor' },
];

const BOTTLE_SHAPES = [
  { value: 'bordelesa', label: 'Bordelesa' },
  { value: 'borgona', label: 'Borgoña' },
  { value: 'rin', label: 'Rin / Alsaciana' },
  { value: 'champan', label: 'Champán / Cava' },
  { value: 'otra', label: 'Otra' },
];

const TYPES = [
  { value: 'tinto', label: 'Tinto' },
  { value: 'blanco', label: 'Blanco' },
  { value: 'rosado', label: 'Rosado' },
  { value: 'espumante', label: 'Espumante' },
  { value: 'naranjo', label: 'Naranjo' },
  { value: 'dulce', label: 'Dulce' },
  { value: 'fortificado', label: 'Fortificado' },
];

export function WineFormPage() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const editId = params.id ? Number(params.id) : undefined;
  const isEditing = !!editId;

  const { data: wine } = useWine(editId);
  const { data: wineries } = useWineries();
  const { data: places } = usePlaces();
  const { data: grapes } = useGrapeVarieties();
  const { data: producers } = useProducers();
  const { data: enologists } = useEnologists();
  // Lista para duplicar desde un vino existente (solo en alta).
  const { data: allWines } = useWines({ per_page: 200, page: 1 });
  const save = useSaveWine();

  const [form, setForm] = useState<FormState>(empty);
  const [purchase, setPurchase] = useState<PurchaseFormState>(emptyPurchase);
  const [duplicateFromId, setDuplicateFromId] = useState<number | null>(null);

  // Staging desde el flow "Probar vino" con foto.
  const fromPhoto = searchParams.get('from_photo') === '1';
  const staging = usePhotoStagingStore();

  // Carga inicial cuando editamos un vino existente.
  useEffect(() => {
    if (wine && editId) {
      setForm({
        name: wine.name,
        winery_id: wine.winery?.id ?? '',
        winery_name_new: '',
        place_id: wine.place?.id ?? '',
        place_path_new: '',
        vintage: wine.vintage ?? '',
        type: wine.type,
        classification: wine.classification,
        alcohol: wine.alcohol ?? '',
        ph: wine.ph ?? '',
        inv_number: wine.inv_number ?? '',
        external_ref: wine.external_ref ?? '',
        format: wine.format,
        bottle_shape: wine.bottle_shape ?? '',
        notes: wine.notes ?? '',
        grape_varieties:
          wine.grape_varieties?.map((g) => ({ id: g.id, percentage: g.percentage })) ?? [],
        producers: wine.producers?.map((p) => ({ id: p.id, role: p.role ?? undefined })) ?? [],
        enologists: wine.enologists?.map((e) => ({ id: e.id, role: e.role ?? undefined })) ?? [],
      });
    }
  }, [wine, editId]);

  // ?duplicate_from=ID en la URL precarga desde un vino existente (en alta).
  useEffect(() => {
    if (isEditing) return;
    const dup = searchParams.get('duplicate_from');
    if (dup) {
      const id = Number(dup);
      if (!Number.isNaN(id)) setDuplicateFromId(id);
    }
  }, [searchParams, isEditing]);

  // Cargamos el vino origen del duplicado por id (no por listado paginado, porque
  // si la cava tiene más de N vinos el .find no lo encuentra).
  const { data: duplicateSource } = useWine(duplicateFromId ?? undefined);

  // Pre-cargar desde el staging del flow foto.
  useEffect(() => {
    if (!fromPhoto || !staging.extracted || isEditing) return;
    const e = staging.extracted;
    setForm((f) => ({
      ...f,
      name: e.name ?? '',
      winery_name_new: e.winery ?? '',
      vintage: e.vintage ?? '',
      type: e.type ?? 'tinto',
      classification: e.classification ?? 'varietal',
      alcohol: e.alcohol ?? '',
      ph: e.ph ?? '',
      inv_number: e.inv_number ?? '',
      format: e.format ?? '750 ml',
      bottle_shape: e.bottle_shape ?? '',
      notes: e.notes ?? '',
      place_path_new: e.place ?? '',
    }));
    // Si el usuario eligió "agregar a cava" o "ambos", arrancamos con la compra
    // expandida y la cantidad pre-cargada en 1.
    if (staging.decision === 'cellar' || staging.decision === 'both') {
      setPurchase((p) => ({ ...p, enabled: true, quantity: p.quantity || 1 }));
    }
  }, [fromPhoto, staging.extracted, staging.decision, isEditing]);

  useEffect(() => {
    if (!duplicateSource || isEditing) return;
    setForm({
      // No copiamos external_ref ni vintage: son lo que cambia entre vinos.
      name: duplicateSource.name,
      winery_id: duplicateSource.winery?.id ?? '',
      winery_name_new: '',
      place_id: duplicateSource.place?.id ?? '',
      place_path_new: '',
      vintage: '',
      type: duplicateSource.type,
      classification: duplicateSource.classification,
      alcohol: duplicateSource.alcohol ?? '',
      ph: duplicateSource.ph ?? '',
      inv_number: duplicateSource.inv_number ?? '',
      external_ref: '',
      format: duplicateSource.format,
      bottle_shape: duplicateSource.bottle_shape ?? '',
      notes: duplicateSource.notes ?? '',
      grape_varieties:
        duplicateSource.grape_varieties?.map((g) => ({ id: g.id, percentage: g.percentage })) ?? [],
      producers:
        duplicateSource.producers?.map((p) => ({ id: p.id, role: p.role ?? undefined })) ?? [],
      enologists:
        duplicateSource.enologists?.map((e) => ({ id: e.id, role: e.role ?? undefined })) ?? [],
    });
  }, [duplicateSource, isEditing]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 1. Resolver winery_id: si no eligió una existente y tipeó un nombre nuevo,
    //    lo creamos antes de guardar el wine.
    let wineryId: number | null = form.winery_id || null;
    if (!wineryId && form.winery_name_new.trim()) {
      try {
        const resp: any = await api.post('/wineries', {
          name: form.winery_name_new.trim(),
        });
        wineryId = resp.data.data.id;
      } catch (err: any) {
        // Si falla por duplicado, refrescamos y dejamos al usuario elegir.
        alert('No se pudo crear la bodega "' + form.winery_name_new + '": ' + (err?.response?.data?.message ?? err.message));
        return;
      }
    }

    // 2. Resolver place_id desde un path "Argentina > Mendoza > ..."
    let placeId: number | null = form.place_id || null;
    if (!placeId && form.place_path_new.trim()) {
      const parts = form.place_path_new.split('>').map((s) => s.trim()).filter(Boolean);
      let parentId: number | null = null;
      for (let i = 0; i < parts.length; i++) {
        const type = i === 0 ? 'country' : 'region';
        try {
          const resp: any = await api.post('/places', {
            parent_id: parentId,
            type,
            name: parts[i],
          });
          parentId = resp.data.data.id;
        } catch {
          // Si ya existe, queda como está. Mejor no romper el guardado del wine.
          parentId = null;
          break;
        }
      }
      placeId = parentId;
    }

    const payload: any = {
      name: form.name,
      winery_id: wineryId,
      place_id: placeId,
      vintage: form.vintage || null,
      type: form.type,
      classification: form.classification,
      alcohol: form.alcohol === '' ? null : Number(form.alcohol),
      ph: form.ph === '' ? null : Number(form.ph),
      inv_number: form.inv_number || null,
      external_ref: form.external_ref || null,
      format: form.format,
      bottle_shape: form.bottle_shape || null,
      notes: form.notes || null,
      is_in_cellar: fromPhoto ? staging.decision !== 'tasted_only' : true,
      grape_varieties: form.grape_varieties.length ? form.grape_varieties : undefined,
      producers: form.producers.length ? form.producers : undefined,
      enologists: form.enologists.length ? form.enologists : undefined,
    };

    try {
      const saved = await save.mutateAsync({ id: editId, payload });

      // 3. Subir fotos del staging (si vinieron del flow Probar vino).
      if (fromPhoto && staging.photos.length > 0 && !editId) {
        for (let i = 0; i < staging.photos.length; i++) {
          const fd = new FormData();
          fd.append('file', staging.photos[i]);
          fd.append('kind', i === 0 ? 'front' : i === 1 ? 'back' : 'other');
          try {
            await api.post(`/wines/${saved.id}/images`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          } catch {
            // Continuar aunque falle una foto; mejor tener el wine que cancelar todo.
          }
        }
      }

      // 4. Registrar la compra si el usuario llenó la sección.
      if (purchase.enabled && purchase.quantity && Number(purchase.quantity) > 0) {
        try {
          await api.post('/purchases', {
            wine_id: saved.id,
            purchase_date: purchase.purchase_date,
            quantity: Number(purchase.quantity),
            unit_price: purchase.unit_price === '' ? 0 : Number(purchase.unit_price),
            currency: purchase.currency,
            supplier_text: purchase.supplier_name.trim() || null,
            notes: purchase.notes.trim() || null,
            acquisition_type: 'purchase',
            lots: purchase.location_id
              ? [{
                  location_id: purchase.location_id,
                  quantity: Number(purchase.quantity),
                }]
              : undefined,
          });
        } catch (err: any) {
          alert('El vino se guardó pero la compra falló: ' + (err?.response?.data?.message ?? err.message));
        }
      }

      // 5. Si vino del flow "ambos" (probar + cava), también una tasting_note.
      if (fromPhoto && staging.decision === 'both' && !editId) {
        try {
          await api.post('/tasting-notes', {
            wine_id: saved.id,
            tasted_on: today(),
            score: staging.score,
            scale: '100',
            tasting_event_id: staging.eventId,
          });
        } catch {
          // Ignorar — el wine quedó OK.
        }
      }

      // 6. Limpiar staging y navegar al detalle.
      if (fromPhoto) staging.clear();
      navigate(`/wines/${saved.id}`);
    } catch (err: any) {
      alert('No se pudo guardar el vino: ' + (err?.response?.data?.message ?? err.message));
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl">
      <h1 className="text-2xl font-medium mb-4 md:mb-6">
        {isEditing ? 'Editar vino' : fromPhoto ? 'Nuevo vino desde foto' : 'Nuevo vino'}
      </h1>

      {/* Banner cuando viene del flow "Probar vino" */}
      {fromPhoto && staging.extracted && (
        <div className="card p-4 mb-4 border-emerald-200 bg-emerald-50">
          <p className="text-sm font-medium text-emerald-900 inline-flex items-center gap-2">
            <Sparkles size={14} /> Datos extraídos por IA — revisá y completá lo que falte
          </p>
          {staging.photos.length > 0 && (
            <div className="mt-3 flex gap-2 overflow-x-auto">
              {staging.photos.map((p, i) => (
                <img
                  key={i}
                  src={URL.createObjectURL(p)}
                  alt=""
                  className="h-20 w-20 object-cover rounded border border-notion-200 shrink-0"
                />
              ))}
              <span className="text-[11px] text-notion-500 self-center">
                Las fotos se guardan automáticamente al confirmar.
              </span>
            </div>
          )}
        </div>
      )}

      {!isEditing && !fromPhoto && (
        <div className="card p-4 mb-4">
          <label className="label flex items-center gap-2">
            <Copy size={14} /> Duplicar desde un vino existente
          </label>
          <Combobox
            value={duplicateFromId}
            placeholder="Buscar por nombre, bodega…"
            options={
              allWines?.data?.map((w) => ({
                id: w.id,
                label: `${w.name}${w.vintage ? ` ${w.vintage}` : ''} · ${w.format}`,
                hint: w.winery?.name ?? undefined,
              })) ?? []
            }
            onChange={(id) => setDuplicateFromId(id ? Number(id) : null)}
          />
          <p className="text-[11px] text-notion-500 mt-1">
            Copia bodega, cepas, productores, enólogos, tipo, formato y notas. La añada y el
            ID externo los completás vos.
          </p>
        </div>
      )}

      <form onSubmit={submit} className="card p-6 space-y-5">
        <div>
          <label className="label">Nombre</label>
          <input
            className="input"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="label">Bodega</label>
            <Combobox
              value={form.winery_id || null}
              placeholder="Buscar bodega existente…"
              options={(wineries ?? []).map((w) => ({ id: w.id, label: w.name }))}
              onChange={(id) => setForm({ ...form, winery_id: id ? Number(id) : '' })}
            />
            {!form.winery_id && (
              <input
                className="input mt-1"
                placeholder="…o tipear una bodega nueva"
                value={form.winery_name_new}
                onChange={(e) => setForm({ ...form, winery_name_new: e.target.value })}
              />
            )}
          </div>
          <div>
            <label className="label">Procedencia</label>
            <Combobox
              value={form.place_id || null}
              placeholder="Buscar procedencia existente…"
              options={(places ?? []).map((p) => ({ id: p.id, label: p.display_path ?? p.name }))}
              onChange={(id) => setForm({ ...form, place_id: id ? Number(id) : '' })}
            />
            {!form.place_id && (
              <input
                className="input mt-1"
                placeholder="…o tipear: Argentina > Mendoza > Valle de Uco"
                value={form.place_path_new}
                onChange={(e) => setForm({ ...form, place_path_new: e.target.value })}
              />
            )}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3">
          <div>
            <label className="label">Añada</label>
            <input
              type="number"
              className="input"
              value={form.vintage}
              onChange={(e) =>
                setForm({ ...form, vintage: e.target.value ? Number(e.target.value) : '' })
              }
            />
          </div>
          <div>
            <label className="label">Tipo</label>
            <select
              className="input"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
            >
              {TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Clasificación</label>
            <select
              className="input"
              value={form.classification}
              onChange={(e) => setForm({ ...form, classification: e.target.value })}
            >
              <option value="varietal">Varietal</option>
              <option value="blend">Blend</option>
            </select>
          </div>
          <div>
            <label className="label">Capacidad</label>
            <input
              className="input"
              list="bottle-formats"
              value={form.format}
              onChange={(e) => setForm({ ...form, format: e.target.value })}
              placeholder="750 ml"
            />
            <datalist id="bottle-formats">
              {BOTTLE_FORMATS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </datalist>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3">
          <div>
            <label className="label">Forma de botella</label>
            <select
              className="input"
              value={form.bottle_shape}
              onChange={(e) => setForm({ ...form, bottle_shape: e.target.value })}
            >
              <option value="">—</option>
              {BOTTLE_SHAPES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Alcohol (%)</label>
            <input
              type="number"
              step="0.1"
              className="input"
              value={form.alcohol}
              onChange={(e) =>
                setForm({ ...form, alcohol: e.target.value === '' ? '' : Number(e.target.value) })
              }
            />
          </div>
          <div>
            <label className="label">PH</label>
            <input
              type="number"
              step="0.01"
              className="input"
              value={form.ph}
              onChange={(e) =>
                setForm({ ...form, ph: e.target.value === '' ? '' : Number(e.target.value) })
              }
            />
          </div>
          <div>
            <label className="label">Número INV</label>
            <input
              className="input"
              value={form.inv_number}
              onChange={(e) => setForm({ ...form, inv_number: e.target.value })}
            />
          </div>
        </div>

        <div>
          <label className="label">ID externo (tu referencia)</label>
          <input
            className="input"
            placeholder="V001"
            value={form.external_ref}
            onChange={(e) => setForm({ ...form, external_ref: e.target.value })}
          />
          <p className="text-[11px] text-notion-500 mt-1">
            Tu identificador (V001, V002…). Es el que vas a usar como wine_ref en los imports
            de Compras y Precios.
          </p>
        </div>

        <div>
          <label className="label">Cepas</label>
          <TagsCombobox
            selected={form.grape_varieties.map((g) => g.id)}
            options={(grapes ?? []).map((g) => ({ id: g.id, label: g.name }))}
            onChange={(ids) =>
              setForm({ ...form, grape_varieties: ids.map((id) => ({ id })) })
            }
            placeholder="Buscar cepa…"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="label">Productores</label>
            <RoleTagsCombobox
              selected={form.producers}
              options={(producers ?? []).map((p) => ({ id: p.id, label: p.name }))}
              roles={PERSON_ROLES}
              onChange={(next) => setForm({ ...form, producers: next })}
              placeholder="Buscar productor…"
            />
          </div>
          <div>
            <label className="label">Enólogos</label>
            <RoleTagsCombobox
              selected={form.enologists}
              options={(enologists ?? []).map((e) => ({ id: e.id, label: e.name }))}
              roles={PERSON_ROLES}
              onChange={(next) => setForm({ ...form, enologists: next })}
              placeholder="Buscar enólogo…"
            />
          </div>
        </div>

        <div>
          <label className="label">Notas</label>
          <textarea
            className="input"
            rows={3}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>

        {/* Sección Registrar la compra ahora — solo en alta */}
        {!isEditing && (
          <div className="border-t border-notion-100 pt-4">
            <label className="inline-flex items-center gap-2 text-sm font-medium text-notion-800 cursor-pointer">
              <input
                type="checkbox"
                checked={purchase.enabled}
                onChange={(e) => setPurchase({ ...purchase, enabled: e.target.checked })}
                className="w-4 h-4"
              />
              Registrar la compra ahora
            </label>
            <p className="text-[11px] text-notion-500 ml-6 mt-0.5">
              Si ya tenés las botellas, completá los datos de la compra para que entren al stock.
            </p>

            {purchase.enabled && (
              <div className="mt-3 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Fecha de compra</label>
                    <input
                      type="date"
                      className="input"
                      value={purchase.purchase_date}
                      max={today()}
                      onChange={(e) => setPurchase({ ...purchase, purchase_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="label">Cantidad (botellas)</label>
                    <input
                      type="number"
                      min={1}
                      inputMode="numeric"
                      className="input"
                      value={purchase.quantity}
                      onChange={(e) =>
                        setPurchase({
                          ...purchase,
                          quantity: e.target.value === '' ? '' : Number(e.target.value),
                        })
                      }
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label className="label">Precio unitario</label>
                    <input
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      className="input"
                      value={purchase.unit_price}
                      onChange={(e) =>
                        setPurchase({
                          ...purchase,
                          unit_price: e.target.value === '' ? '' : Number(e.target.value),
                        })
                      }
                      placeholder="0 para regalo / muestra"
                    />
                  </div>
                  <div>
                    <label className="label">Moneda</label>
                    <select
                      className="input"
                      value={purchase.currency}
                      onChange={(e) => setPurchase({ ...purchase, currency: e.target.value })}
                    >
                      <option value="ARS">ARS</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="label">Lugar de compra</label>
                  <input
                    className="input"
                    placeholder="ej. Sotelo, Ozono Drinks, Bodega directo…"
                    value={purchase.supplier_name}
                    onChange={(e) => setPurchase({ ...purchase, supplier_name: e.target.value })}
                  />
                </div>

                <StorageLocationPicker
                  value={purchase.location_id}
                  onChange={(id) => setPurchase({ ...purchase, location_id: id })}
                />
                <p className="text-[11px] text-notion-500 -mt-2">
                  Si todavía no creaste lugares, podés hacerlo desde el menú "Lugares" o
                  crear uno aquí mismo con "Nueva ubicación".
                </p>

                <div>
                  <label className="label">Notas de la compra</label>
                  <textarea
                    className="input"
                    rows={2}
                    value={purchase.notes}
                    onChange={(e) => setPurchase({ ...purchase, notes: e.target.value })}
                    placeholder="ej. comprado con 10% off, reposición, recomendado por…"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            {save.isPending ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
}
